import { Link } from 'react-router';
import { Banknote, CreditCard, Repeat, CheckCircle2 } from 'lucide-react';
import { UniversalPage, Card, Button } from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { useHostingSubscriptions } from '@/domains/billing';

/**
 * Payment methods page — dual-rail + auto-pay.
 *
 * Card / Instant EFT payments via PayFast went live in Phase 9
 * (`_bmad-output/PHASE_9_PAYFAST_BRD.md`). EFT remains available for
 * customers who prefer it.
 *
 * Auto-pay for monthly hosting shipped 2026-06-16 (Phase PF-Q3 reversal,
 * BRD `_bmad-output/PHASE_PF_Q3_REVERSAL_BRD_2026-06-16.md`). Cards saved
 * server-side at PayFast; the customer manages the recurring relationship
 * from /app/billing/auto-pay.
 */
export default function PaymentMethodsPage() {
  const { bank } = siteConfig;
  const { subscriptions } = useHostingSubscriptions();
  const activeSubs = subscriptions.filter(
    (s) => s.status === 'active' || s.status === 'pending' || s.status === 'at_risk'
  );
  const hasAutoPay = activeSubs.length > 0;

  return (
    <UniversalPage
      title="Payment methods"
      description="How you can pay your SightHost invoices."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Payment methods' },
      ]}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card interactive={false} className="p-6">
          <div className="mb-3 inline-flex items-center gap-2 text-primary">
            <CreditCard className="size-5" aria-hidden />
            <h3 className="text-base font-semibold text-ink">Card / Instant EFT (PayFast)</h3>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Pay any unpaid invoice by Visa, Mastercard, Instant EFT, SnapScan,
            or Zapper through PayFast. Look for the <span className="font-medium text-ink">Pay now</span>{' '}
            button on each invoice — payments are confirmed within a minute.
          </p>
          <div className="mt-4">
            <Button asChild size="sm" variant="outline">
              <Link to="/app/billing">View my invoices</Link>
            </Button>
          </div>
        </Card>

        <Card interactive={false} className="p-6">
          <div className="mb-3 inline-flex items-center gap-2 text-primary">
            <Repeat className="size-5" aria-hidden />
            <h3 className="text-base font-semibold text-ink">Auto-pay (monthly hosting)</h3>
          </div>
          {hasAutoPay ? (
            <>
              <p className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-success">
                <CheckCircle2 className="size-4" aria-hidden />
                Subscribed via PayFast — {activeSubs.length} hosting account
                {activeSubs.length === 1 ? '' : 's'} on auto-pay
              </p>
              <p className="mb-4 text-sm text-muted-foreground">
                Your card is charged each month via PayFast Recurring. Card details
                are held by PayFast — not by us. Cancel anytime from your portal.
              </p>
            </>
          ) : (
            <p className="mb-4 text-sm text-muted-foreground">
              Charge your card automatically each month via PayFast Recurring.
              Skip the manual EFT step. Card details are held by PayFast — not
              by us. Cancel anytime from your portal.
            </p>
          )}
          <div className="mt-4">
            <Button asChild size="sm" variant={hasAutoPay ? 'outline' : 'primary'}>
              <Link to="/app/billing/auto-pay">
                {hasAutoPay ? 'Manage auto-pay' : 'Set up auto-pay'}
              </Link>
            </Button>
          </div>
        </Card>

        <Card interactive={false} className="p-6">
          <div className="mb-3 inline-flex items-center gap-2 text-primary">
            <Banknote className="size-5" aria-hidden />
            <h3 className="text-base font-semibold text-ink">EFT (alternative)</h3>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Prefer to pay by EFT? Use the bank details below — be sure to use
            the invoice number as your reference so we can match the payment.
            Settlement appears in your account within 1 business day.
          </p>
          <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Bank</dt>
            <dd className="col-span-2 text-ink">{bank.name}</dd>
            <dt className="text-muted-foreground">Account name</dt>
            <dd className="col-span-2 text-ink">{bank.accountName}</dd>
            <dt className="text-muted-foreground">Account no.</dt>
            <dd className="col-span-2 text-ink tabular-nums">{bank.accountNumber}</dd>
            <dt className="text-muted-foreground">Branch code</dt>
            <dd className="col-span-2 text-ink tabular-nums">{bank.branchCode}</dd>
          </dl>
        </Card>
      </div>
    </UniversalPage>
  );
}
